interface ImagePickerProps {
  images: string[];
  selectedImages: string[];
  onChange: (urls: string[]) => void;
  viewLabels?: boolean;
  disabled?: boolean;
  maxImages?: number;
  viewMode?: 'single' | 'multi';
  strictMultiSlots?: boolean;
}

const VIEW_LABELS_3 = ["Front", "Left", "Back"] as const;
const VIEW_LABELS_4 = ["Front", "Left", "Back", "Right"] as const;

function swapAt<T>(arr: T[], i: number, j: number): T[] {
  const out = [...arr];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Wrapper that saves/restores window scroll position around a state-update callback */
function withScrollPreserve(fn: () => void) {
  const scrollY = window.scrollY;
  fn();
  // Use requestAnimationFrame to restore after React has flushed
  requestAnimationFrame(() => {
    window.scrollTo({ top: scrollY, behavior: 'instant' as ScrollBehavior });
  });
}

export function ImagePicker({
  images,
  selectedImages,
  onChange,
  viewLabels = true,
  disabled,
  maxImages = 6,
  viewMode = 'single',
  strictMultiSlots = false,
}: ImagePickerProps) {
  if (images.length <= 1) return null;

  const viewLabelsList = viewMode === 'multi' && maxImages === 4 ? VIEW_LABELS_4 : VIEW_LABELS_3;
  const slotCount = strictMultiSlots
    ? maxImages
    : Math.min(maxImages, selectedImages.length);

  const toggleImage = (url: string) => {
    if (strictMultiSlots && viewMode === 'multi') {
      // Index-based slot assignment: each slot (Front/Left/Back/Right) is fixed
      const slots = [...selectedImages];
      while (slots.length < maxImages) slots.push('');
      const idx = slots.indexOf(url);
      if (idx >= 0) {
        slots[idx] = '';
        withScrollPreserve(() => onChange(slots));
        return;
      }
      const firstEmpty = slots.findIndex((s) => !s);
      if (firstEmpty >= 0) {
        slots[firstEmpty] = url;
        withScrollPreserve(() => onChange(slots));
      }
      return;
    }
    const idx = selectedImages.indexOf(url);
    if (idx >= 0) {
      withScrollPreserve(() => onChange(selectedImages.filter((u) => u !== url)));
    } else {
      withScrollPreserve(() => onChange([...selectedImages, url].slice(0, maxImages)));
    }
  };

  const getLabel = (url: string) => {
    if (!viewLabels || selectedImages.length < 3) return null;
    const idx = selectedImages.indexOf(url);
    if (idx < 0 || idx >= viewLabelsList.length) return idx >= 0 ? `${idx + 1}` : null;
    return viewLabelsList[idx];
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-700">
          Select images for 3D generation
        </p>
        <p className="text-xs text-slate-500">
          {viewMode === 'multi'
            ? strictMultiSlots
              ? "Fill all 4 required slots in order: Front, Left, Back, Right."
              : `Click images in order: Front, Left, Back${maxImages === 4 ? ', Right' : ''}. Use the arrows below to reorder.`
            : "Choose the photo that best shows the product shape from a straight-on angle."}
        </p>
      </div>

      {/* Reorder strip for multi-view only */}
      {viewMode === 'multi' && viewLabels && slotCount >= (strictMultiSlots ? 1 : 3) && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-3">
          <p className="mb-2 text-xs font-semibold text-indigo-800">Order for 3D ({viewLabelsList.slice(0, slotCount).join(' → ')})</p>
          <div className="flex items-center gap-2">
            {Array.from({ length: slotCount }, (_, i) => i).map((i) => (
              <div key={i} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-xs font-medium text-indigo-600">{viewLabelsList[i]}</span>
                <div className="relative aspect-square w-full max-w-[80px] overflow-hidden rounded-lg border-2 border-indigo-200">
                  {selectedImages[i] ? (
                    <img
                      src={selectedImages[i]}
                      alt={viewLabelsList[i]}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-indigo-100/60 text-[11px] font-semibold text-indigo-500">
                      Empty
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  {i > 0 && (
                    <button
                      type="button"
                      disabled={disabled || !selectedImages[i] || !selectedImages[i - 1]}
                      onClick={() => withScrollPreserve(() => onChange(swapAt(selectedImages, i - 1, i)))}
                      className="rounded bg-indigo-100 p-1 text-indigo-600 hover:bg-indigo-200 disabled:opacity-50"
                      aria-label={`Move ${viewLabelsList[i]} left`}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                  )}
                  {i < slotCount - 1 && (
                    <button
                      type="button"
                      disabled={disabled || !selectedImages[i] || !selectedImages[i + 1]}
                      onClick={() => withScrollPreserve(() => onChange(swapAt(selectedImages, i, i + 1)))}
                      className="rounded bg-indigo-100 p-1 text-indigo-600 hover:bg-indigo-200 disabled:opacity-50"
                      aria-label={`Move ${viewLabelsList[i]} right`}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
        {images.map((url, idx) => {
          const isSelected = selectedImages.includes(url);
          const label = getLabel(url);
          return (
            <button
              key={url}
              type="button"
              disabled={disabled}
              onClick={() => toggleImage(url)}
              aria-label={label ? `Select ${label} view: image ${idx + 1}` : `Select image ${idx + 1}`}
              className={[
                'relative aspect-square overflow-hidden rounded-2xl border-2 transition-all focus:outline-none',
                isSelected
                  ? 'border-indigo-500 ring-2 ring-indigo-300 shadow-lg shadow-indigo-100'
                  : 'border-slate-200 hover:border-indigo-300',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              ].join(' ')}
            >
              <img
                src={url}
                alt={`Product image ${idx + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
              {isSelected && (
                <span className="absolute inset-0 flex flex-col items-center justify-center bg-indigo-500/20 gap-0.5">
                  {label && (
                    <span className="rounded-full bg-indigo-500 px-2 py-0.5 text-xs font-bold text-white">
                      {label}
                    </span>
                  )}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white shadow">
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
